/**
 * Browser Notification + in-app toast handler for the cron-driven
 * `appointment.imminent` SSE event.
 *
 * Returns:
 *  - `handler`: pass to `useAgentEventStream` / `useClientEventStream`
 *    as the `onEventType` entry for `"appointment.imminent"`. Idempotent;
 *    same event fired twice produces one notification (we de-dup by
 *    appointment_id within the current page session).
 *  - `lastToast`: a snapshot of the most recent reminder so callers can
 *    render an inline banner. Auto-clears after `TOAST_TTL_MS`.
 *  - `dismissToast`: callback to drop the toast manually.
 *
 * Browser-Notification permission is requested lazily — the first
 * time an appointment-imminent event arrives we call
 * `Notification.requestPermission()` if permission is still `default`.
 * Once denied or granted we never re-prompt. The toast always renders
 * regardless of permission state so the user never misses a reminder.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import type { RealtimeEvent } from './useEventStream';

const TOAST_TTL_MS = 60_000;

export interface AppointmentReminderToast {
  appointmentId: string;
  startsAt: string;
  endsAt: string;
  purpose: string | null;
  /** Counterparty display name — agent's name when shown to the
   *  client, client's name when shown to the agent. */
  counterpartyName: string;
  /** "agent" | "client" — perspective of the viewer. */
  viewerRole: 'agent' | 'client';
}

interface ImminentExtra {
  appointment_id: string;
  starts_at: string;
  ends_at: string;
  purpose: string | null;
  agent_id: string | null;
  agent_name: string | null;
  client_id: string;
  client_name: string;
}

function readExtra(event: RealtimeEvent<string>): ImminentExtra | null {
  const raw = event.extra;
  if (!raw || typeof raw !== 'object') return null;
  const e = raw as Partial<ImminentExtra>;
  if (
    typeof e.appointment_id !== 'string' ||
    typeof e.starts_at !== 'string' ||
    typeof e.ends_at !== 'string' ||
    typeof e.client_id !== 'string' ||
    typeof e.client_name !== 'string'
  ) {
    return null;
  }
  return {
    appointment_id: e.appointment_id,
    starts_at: e.starts_at,
    ends_at: e.ends_at,
    purpose: e.purpose ?? null,
    agent_id: e.agent_id ?? null,
    agent_name: e.agent_name ?? null,
    client_id: e.client_id,
    client_name: e.client_name,
  };
}

function formatMinutesUntil(startsAt: string): string {
  const ms = new Date(startsAt).getTime() - Date.now();
  const minutes = Math.max(0, Math.round(ms / 60_000));
  if (minutes === 0) return 'starting now';
  if (minutes === 1) return 'in 1 minute';
  return `in ${minutes} minutes`;
}

/** Fire a browser Notification if permission allows. Lazy-prompts
 *  on first use. Never blocks; the toast renders independently. */
function showBrowserNotification(title: string, body: string): void {
  if (globalThis.window === undefined || !('Notification' in globalThis)) return;
  const N = globalThis.Notification;
  if (N.permission === 'granted') {
    try {
      new N(title, { body, tag: 'appointment-imminent' });
    } catch {
      // Some browsers throw when called outside a user gesture; the
      // in-app toast still covers the user.
    }
    return;
  }
  if (N.permission === 'default') {
    N.requestPermission().then((perm) => {
      if (perm === 'granted') {
        try {
          new N(title, { body, tag: 'appointment-imminent' });
        } catch {
          /* see above */
        }
      }
    });
  }
  // 'denied' → nothing to do.
}

export function useAppointmentReminder(viewerRole: 'agent' | 'client'): {
  handler: (event: RealtimeEvent<string>) => void;
  lastToast: AppointmentReminderToast | null;
  dismissToast: () => void;
} {
  const [lastToast, setLastToast] = useState<AppointmentReminderToast | null>(null);
  const seenIdsRef = useRef<Set<string>>(new Set());
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear the auto-dismiss timer on unmount.
  useEffect(() => {
    return () => {
      if (dismissTimer.current !== null) clearTimeout(dismissTimer.current);
    };
  }, []);

  const dismissToast = useCallback(() => {
    setLastToast(null);
    if (dismissTimer.current !== null) {
      clearTimeout(dismissTimer.current);
      dismissTimer.current = null;
    }
  }, []);

  const handler = useCallback(
    (event: RealtimeEvent<string>): void => {
      const extra = readExtra(event);
      if (!extra) return;
      // Idempotent: if we've already toasted this appointment in this
      // page session, skip. The backend Redis dedupe handles
      // cross-session double-firing.
      if (seenIdsRef.current.has(extra.appointment_id)) return;
      seenIdsRef.current.add(extra.appointment_id);

      const counterpartyName =
        viewerRole === 'agent' ? extra.client_name : (extra.agent_name ?? 'your Account Executive');

      const toast: AppointmentReminderToast = {
        appointmentId: extra.appointment_id,
        startsAt: extra.starts_at,
        endsAt: extra.ends_at,
        purpose: extra.purpose,
        counterpartyName,
        viewerRole,
      };
      setLastToast(toast);

      if (dismissTimer.current !== null) clearTimeout(dismissTimer.current);
      dismissTimer.current = setTimeout(() => {
        setLastToast(null);
        dismissTimer.current = null;
      }, TOAST_TTL_MS);

      const when = formatMinutesUntil(extra.starts_at);
      const title = `Call with ${counterpartyName} ${when}`;
      const body = extra.purpose ?? 'Scheduled callback.';
      showBrowserNotification(title, body);
    },
    [viewerRole],
  );

  return { handler, lastToast, dismissToast };
}
